import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PawPrint, ArrowRight, Heart, Shield, Users, Sparkles,
  Phone, Mail, Twitter, Instagram, Github, ExternalLink,
  Zap, BookOpen, Bell, MapPin, Package, Truck, CheckCircle,
  AlertTriangle, Calendar, FileText, Download, Smartphone,
  Home, Backpack, ClipboardList, User, Building2, ShieldAlert,
  GraduationCap, Award,
} from "lucide-react";
import { motion } from "framer-motion";

// Logo URLs - Official brand assets
const LOGO_HUB = "/logos/logo-emergencia-animal.png";
const LOGO_CONECTA = "/logos/logo-conecta.png";
const LOGO_PREPARA = "/logos/logo-prepara.png";

const APPS = [
  {
    id: "conecta",
    name: "Emergencia Animal: Conecta",
    tagline: "Cuando cada minuto cuenta",
    description: "Plataforma que conecta voluntarios con protectoras, refugios, santuarios y colonias felinas durante emergencias. Sistema de alertas en tiempo real, geolocalización, chat directo y gamificación para la comunidad solidaria.",
    color: "#686af3", // Azul vibrante
    accentColor: "#686af3", // Púrpura más oscuro
    gradient: "from-[#686af3] to-[#8688f5]",
    bgGradient: "bg-gradient-to-br from-[#686af3]/10 to-[#8688f5]/5",
    logo: LOGO_CONECTA,
    features: [
      { icon: Bell, text: "Alertas en tiempo real" },
      { icon: MapPin, text: "Geolocalización" },
      { icon: Users, text: "Red de voluntarios" },
      { icon: Heart, text: "Sistema de ayudas" },
    ],
    href: "/conecta",
    status: "live",
    cta: "Entrar a Conecta",
  },
  {
    id: "prepara",
    name: "Emergencia Animal: Prepara",
    tagline: "Prevenir es proteger",
    description: "Planes personalizados de emergencia para particulares y protectoras. Crea tu tarjeta de domicilio para alertar a equipos de rescate, prepara la mochila de vida con checklists adaptados a cada emergencia, y recibe guías paso a paso de qué hacer antes, durante y después de cualquier crisis.",
    color: "#81801a", // Verde oliva (calma, preparación)
    accentColor: "#a09f2a", // Verde oliva claro
    gradient: "from-[#81801a] to-[#a09f2a]",
    bgGradient: "bg-gradient-to-br from-[#81801a]/10 to-[#a09f2a]/5",
    logo: LOGO_PREPARA,
    features: [
      { icon: Home, text: "Tarjeta de domicilio" },
      { icon: Backpack, text: "Mochila de vida" },
      { icon: ClipboardList, text: "Checklists por emergencia" },
      { icon: FileText, text: "Guías antes/durante/después" },
    ],
    href: "/prepara",
    status: "live",
    cta: "Entrar a Prepara",
  },
  {
    id: "aprende",
    name: "Emergencia Animal: Aprende",
    tagline: "El conocimiento salva vidas",
    description: "Plataforma de formación online con cursos basados en programas oficiales de FEMA. Aprende a proteger a tus animales antes, durante y después de emergencias. Cursos para familias, protectoras y voluntarios con quizzes interactivos y certificados.",
    color: "#7c3aed", // Púrpura (educación, formación)
    accentColor: "#8b5cf6", // Púrpura claro
    gradient: "from-[#7c3aed] to-[#8b5cf6]",
    bgGradient: "bg-gradient-to-br from-[#7c3aed]/10 to-[#8b5cf6]/5",
    logo: LOGO_HUB, // Using hub logo for now
    features: [
      { icon: GraduationCap, text: "Cursos certificados" },
      { icon: BookOpen, text: "Contenido FEMA adaptado" },
      { icon: Award, text: "Certificados descargables" },
      { icon: Users, text: "3 tracks de formación" },
    ],
    href: "/aprende",
    status: "live",
    cta: "Entrar a Aprende",
  },
];

// Team members
const TEAM = [
  {
    name: "Núria Querol",
    role: "Dirección",
    avatar: "/avatars/avatar-circle-nobg.png",
    color: "#a88efb", // Lila - su color favorito
    isImage: true,
  },
  {
    name: "E1",
    role: "Desarrollo",
    avatar: "/avatars/avatar-e1-nobg.png",
    color: "#686af3",
    isImage: true,
  },
];

export default function HubLanding() {
  const navigate = useNavigate();
  const [hoveredApp, setHoveredApp] = useState(null);

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[var(--purple)] opacity-[0.03] blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[var(--blue-vibrant)] opacity-[0.03] blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[var(--coral)] opacity-[0.02] blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Logo with subtle pulse animation */}
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#686af3] to-[#22c55e] rounded-2xl blur-xl opacity-30 animate-pulse" />
              <div className="relative w-12 h-12 rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
                <img 
                  src={LOGO_HUB} 
                  alt="Emergencia Animal" 
                  className="w-full h-full object-contain"
                />
              </div>
            </motion.div>
            <div>
              <h1 className="font-heading text-xl font-bold text-[var(--navy)]">
                Emergencia Animal
              </h1>
              <p className="font-mono text-[10px] text-[var(--purple)] tracking-wider">
                NADIE SE QUEDA ATRÁS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="#apps" className="font-body text-sm text-slate-500 hover:text-[var(--blue-vibrant)] transition-colors hidden sm:block">
              Apps
            </a>
            <a href="#about" className="font-body text-sm text-slate-500 hover:text-[var(--blue-vibrant)] transition-colors hidden sm:block">
              Sobre nosotros
            </a>
            <a href="#team" className="font-body text-sm text-slate-500 hover:text-[var(--blue-vibrant)] transition-colors hidden sm:block">
              Equipo
            </a>
            <Button
              onClick={() => navigate("/conecta")}
              className="bg-[var(--blue-vibrant)] hover:bg-[#5658cc] text-white rounded-full px-6 text-sm"
              data-testid="hub-header-access-btn"
            >
              Acceder
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-[var(--purple)]/10 text-[var(--purple)] border-0 mb-6 px-4 py-1.5 text-sm">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Plataforma solidaria para emergencias
            </Badge>
            
            {/* Animated Logo */}
            <div className="mb-8">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
                className="relative inline-block"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#686af3] via-[#a88efb] to-[#686af3] rounded-3xl blur-2xl opacity-40 animate-pulse scale-110" />
                
                {/* Floating rings */}
                <motion.div
                  className="absolute -inset-4 border-2 border-[#686af3]/20 rounded-[2rem]"
                  animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.3, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                  className="absolute -inset-8 border border-[#a88efb]/10 rounded-[2.5rem]"
                  animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.1, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                />
                
                {/* Logo container */}
                <div className="relative w-36 h-36 mx-auto rounded-3xl bg-white shadow-2xl shadow-purple-500/30 flex items-center justify-center p-3">
                  <img 
                    src={LOGO_HUB} 
                    alt="Emergencia Animal" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </motion.div>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-[var(--navy)] mb-6 leading-tight">
              Tecnología al servicio
              <br />
              <span className="bg-gradient-to-r from-[#686af3] via-[#a88efb] to-[#686af3] bg-clip-text text-transparent">
                de los animales
              </span>
            </h1>
            
            <p className="font-body text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
              Un ecosistema de aplicaciones diseñado para proteger a los animales 
              antes, durante y después de cualquier emergencia. Porque cuando se trata 
              de vidas, <span className="text-[var(--navy)] font-semibold">nadie se queda atrás</span>.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => navigate("/conecta")}
                className="bg-gradient-to-r from-[#686af3] to-[#a88efb] hover:opacity-90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#686af3]/25"
                data-testid="hub-hero-explore-btn"
              >
                Explorar apps
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-slate-200 text-slate-600 hover:border-[var(--purple)] hover:text-[var(--purple)] rounded-full px-8 py-6 text-base font-semibold"
                data-testid="hub-hero-about-btn"
              >
                Conocer más
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apps Section */}
      <section id="apps" className="relative z-10 py-20 bg-white/50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#686af3]/10 text-[#686af3] border-0 mb-4">
              Nuestras aplicaciones
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[var(--navy)] mb-4">
              Tres apps, una misión
            </h2>
            <p className="font-body text-slate-500 max-w-lg mx-auto">
              Preparación, respuesta y formación. Todo lo que necesitas para proteger a los animales.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {APPS.map((app, idx) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
                onMouseEnter={() => setHoveredApp(app.id)}
                onMouseLeave={() => setHoveredApp(null)}
                className="group relative"
                data-testid={`hub-app-card-${app.id}`}
              >
                <div className={`
                  relative bg-white rounded-3xl p-8 shadow-xl border border-slate-100
                  transition-all duration-500 overflow-hidden
                  ${hoveredApp === app.id ? 'shadow-2xl -translate-y-2' : ''}
                `}>
                  {/* Gradient overlay on hover - pointer-events-none to allow clicks through */}
                  <div 
                    className={`
                      absolute inset-0 bg-gradient-to-br ${app.gradient} opacity-0 
                      group-hover:opacity-[0.05] transition-opacity duration-500 pointer-events-none
                    `}
                  />
                  
                  {/* Status badge */}
                  {app.status === "coming" && (
                    <div className="absolute top-6 right-6">
                      <Badge className="bg-[#81801a]/10 text-[#81801a] border-0 text-xs font-semibold">
                        Próximamente
                      </Badge>
                    </div>
                  )}
                  {app.status === "live" && (
                    <div className="absolute top-6 right-6">
                      <Badge className="bg-green-100 text-green-600 border-0 text-xs flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Activa
                      </Badge>
                    </div>
                  )}

                  {/* Logo */}
                  <div 
                    className={`
                      w-20 h-20 rounded-2xl bg-white 
                      flex items-center justify-center mb-6 shadow-lg border border-slate-100
                      transition-transform duration-500 group-hover:scale-110 overflow-hidden
                    `}
                    style={{ boxShadow: `0 8px 24px ${app.color}20` }}
                  >
                    <img 
                      src={app.logo} 
                      alt={app.name}
                      className="w-full h-full object-contain p-1"
                    />
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-2xl font-bold text-[var(--navy)] mb-2">
                    {app.name}
                  </h3>
                  <p className="font-body text-sm mb-4" style={{ color: app.color }}>
                    {app.tagline}
                  </p>
                  <p className="font-body text-slate-500 mb-6 leading-relaxed text-sm">
                    {app.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {app.features.map((feature, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-2 text-sm text-slate-500"
                      >
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${app.color}10` }}
                        >
                          <feature.icon className="w-4 h-4" style={{ color: app.color }} />
                        </div>
                        <span className="font-body text-xs">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => app.status === "live" && navigate(app.href)}
                    disabled={app.status === "coming"}
                    className={`
                      w-full rounded-full py-6 font-semibold transition-all
                      ${app.status === "live" 
                        ? `bg-gradient-to-r ${app.gradient} text-white hover:opacity-90 shadow-lg` 
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }
                    `}
                    style={app.status === "live" ? { boxShadow: `0 8px 24px ${app.color}30` } : {}}
                    data-testid={`hub-app-cta-${app.id}`}
                  >
                    {app.cta}
                    {app.status === "live" && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Badge className="bg-[#a88efb]/10 text-[#a88efb] border-0 mb-4">
              Nuestra misión
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[var(--navy)] mb-6">
              Sobre Emergencia Animal
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100"
            data-testid="hub-about-section"
          >
            <div className="prose prose-lg max-w-none">
              <h3 className="font-heading text-xl font-bold text-[var(--navy)] mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#686af3]" />
                Misión
              </h3>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Proteger a los animales <strong className="text-[var(--navy)]">antes, durante y después</strong> de 
                cualquier emergencia, conectando a quienes quieren ayudar con quienes necesitan ayuda, 
                a través de tecnología accesible y gratuita.
              </p>

              <h3 className="font-heading text-xl font-bold text-[var(--navy)] mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#a88efb]" />
                Visión
              </h3>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Un mundo donde ningún animal quede atrás en una emergencia. Donde particulares, 
                protectoras, refugios, santuarios y colonias felinas tengan las herramientas 
                necesarias para prepararse, responder y recuperarse de cualquier crisis.
              </p>

              <h3 className="font-heading text-xl font-bold text-[var(--navy)] mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#a88efb]" />
                Historia
              </h3>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                <strong className="text-[var(--navy)]">Emergencia Animal</strong> nace de la necesidad 
                de dar una respuesta coordinada y eficiente ante situaciones de crisis que afectan 
                a los animales. Tras ver cómo emergencias como la DANA dejaban a miles de animales 
                desamparados, decidimos crear una solución tecnológica que conecte a quienes 
                quieren ayudar con quienes necesitan ayuda.
              </p>
              <p className="font-body text-slate-600 leading-relaxed">
                Nuestro lema <span className="text-[#a88efb] font-semibold">"Nadie se queda atrás"</span> refleja 
                nuestro compromiso con cada animal, sin importar su especie, situación o ubicación. 
                Todas nuestras aplicaciones son <strong className="text-[var(--navy)]">gratuitas</strong> y están 
                diseñadas pensando en la accesibilidad y facilidad de uso para toda la comunidad.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-slate-100">
              {[
                { value: "100%", label: "Gratuito", color: "#686af3" },
                { value: "24/7", label: "Disponible", color: "#a88efb" },
                { value: "∞", label: "Compromiso", color: "#686af3" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p 
                    className="font-heading text-3xl font-bold"
                    style={{ color: stat.color }}
                  >
                    {stat.value}
                  </p>
                  <p className="font-body text-sm text-slate-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="relative z-10 py-20 bg-gradient-to-b from-white/50 to-[var(--cream)]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Badge className="bg-[#a88efb]/10 text-[#a88efb] border-0 mb-4">
              <Users className="w-3.5 h-3.5 mr-1.5" />
              El equipo
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[var(--navy)] mb-4">
              Quiénes somos
            </h2>
            <p className="font-body text-slate-500 max-w-lg mx-auto">
              Un pequeño equipo con un gran compromiso por los animales
            </p>
          </motion.div>

          <div className="flex flex-col sm:flex-row justify-center gap-8">
            {TEAM.map((member, idx) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
                className="group"
                data-testid={`hub-team-member-${idx}`}
              >
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 text-center transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 min-w-[220px]">
                  {/* Avatar */}
                  <motion.div
                    whileHover={{ scale: 1.05, rotate: 5 }}
                    className="w-24 h-24 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg overflow-hidden"
                    style={{ 
                      backgroundColor: member.isImage ? 'transparent' : `${member.color}15`,
                      boxShadow: `0 8px 24px ${member.color}20`
                    }}
                  >
                    {member.isImage ? (
                      <img 
                        src={member.avatar} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span 
                        className="font-heading text-2xl font-bold"
                        style={{ color: member.color }}
                      >
                        {member.avatar}
                      </span>
                    )}
                  </motion.div>
                  
                  {/* Info */}
                  <h3 className="font-heading text-xl font-bold text-[var(--navy)] mb-1">
                    {member.name}
                  </h3>
                  <p 
                    className="font-body text-sm font-medium"
                    style={{ color: member.color }}
                  >
                    {member.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Fun note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-center font-body text-sm text-slate-400 mt-10"
          >
            Un humano y una IA trabajando juntos por los animales
          </motion.p>
        </div>
      </section>

      {/* Impact Stats Section - Navy Banner */}
      <section className="relative z-10 py-16 md:py-20 bg-[var(--navy)] overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#a88efb] blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#686af3] blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
              Juntos somos más fuertes
            </h2>
            <p className="font-body text-blue-200/70 text-sm">
              El impacto de nuestra comunidad solidaria
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: "500+", label: "Voluntarios activos", icon: Users, color: "#686af3" },
              { value: "50+", label: "Organizaciones conectadas", icon: Building2, color: "#a88efb" },
              { value: "1.2K+", label: "Ayudas completadas", icon: Heart, color: "#da818d" },
              { value: "100+", label: "Emergencias atendidas", icon: ShieldAlert, color: "#fa9000" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                className="text-center group"
              >
                <div 
                  className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-7 h-7" style={{ color: stat.color }} />
                </div>
                <p className="font-heading text-3xl md:text-4xl font-black text-white mb-1">
                  {stat.value}
                </p>
                <p className="font-body text-xs text-blue-200/60">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#686af3] via-[#a88efb] to-[#686af3] rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden"
            data-testid="hub-cta-section"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-white/10 rounded-full translate-x-1/3 translate-y-1/3" />
            
            <div className="relative">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <PawPrint className="w-16 h-16 mx-auto mb-6 opacity-90" />
              </motion.div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                ¿Listo para marcar la diferencia?
              </h2>
              <p className="font-body text-blue-100 mb-8 max-w-lg mx-auto text-lg">
                Únete a nuestra comunidad de personas que hacen posible que ningún animal 
                se quede atrás.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate("/conecta")}
                  className="bg-white text-[#686af3] hover:bg-blue-50 rounded-full px-8 py-6 font-semibold text-base shadow-xl"
                  data-testid="hub-cta-start-btn"
                >
                  Empezar ahora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_HUB} 
                alt="Emergencia Animal" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <p className="font-heading text-sm font-bold text-[var(--navy)]">Emergencia Animal</p>
                <p className="font-mono text-[10px] text-slate-400">Nadie se queda atrás</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {[
                { Icon: Twitter, href: "#", label: "Twitter" },
                { Icon: Instagram, href: "#", label: "Instagram" },
                { Icon: Github, href: "#", label: "GitHub" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-[#686af3] hover:text-white transition-all"
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            
            <p className="font-mono text-xs text-slate-400">
              © 2026 Emergencia Animal · Proyecto altruista            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
